/**
 * Règles de l'affichage optimiste des réponses scannées.
 *
 * Extraites de `SessionLive.jsx` pour être testables seules : ce sont elles qui
 * décident ce que le professeur voit avant que le serveur ait répondu, et une
 * erreur ici afficherait un enregistrement qui n'a pas eu lieu.
 *
 * Principe unique : LE SERVEUR FAIT FOI. L'optimiste ne comble que ce que le
 * serveur n'a pas encore renvoyé, et se retire dès qu'il se prononce.
 */

/**
 * La réponse scannée est-elle juste ? `null` quand la bonne réponse n'est pas
 * connue — la vue n'affiche alors ni ✓ ni ✗, ce qui est l'état honnête. Ne
 * jamais rendre `false` par défaut : ce serait une croix arbitraire sur la
 * copie d'un élève.
 */
export const evaluerReponse = (reponse, bonneReponse) => {
  if (!reponse || !bonneReponse) return null;
  return reponse.toUpperCase() === bonneReponse.toUpperCase();
};

/**
 * La liste que voit le professeur : les réponses du serveur, complétées par les
 * optimistes qu'il n'a pas encore confirmées.
 *
 * `reponses` est renvoyée telle quelle quand il n'y a rien à ajouter — sans ça,
 * un nouveau tableau à chaque rendu invaliderait les `useMemo` de la vue à
 * chaque battement du flux.
 */
export const fusionnerReponses = (reponses, optimistes) => {
  if (!optimistes || optimistes.size === 0) return reponses;
  const connus = new Set(reponses.map((r) => r.code_aruco));
  const complement = [];
  for (const [code, r] of optimistes) {
    if (!connus.has(code)) complement.push(r);
  }
  return complement.length > 0 ? [...reponses, ...complement] : reponses;
};

/**
 * Les cartes que le serveur n'a PAS enregistrées, et qui doivent donc quitter
 * l'affichage optimiste : `already` (une réponse existe déjà, elle arrivera par
 * le flux) et `error` (carte inconnue ou non assignée).
 */
export const codesNonEnregistres = (resultats) => {
  if (!Array.isArray(resultats)) return [];
  return resultats
    .filter((r) => r && r.status !== 'created')
    .map((r) => r.code_aruco);
};
